import { useEffect, useRef } from "react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";

interface SparePartLabelProps {
  qrValue: string;
  partName: string;
  partId: string;
  sourceBrand?: string | null;
  sourceModel?: string | null;
  harvestedAt: string;
  qrSize?: number;
}

export function formatDate(dateStr: string, locale: string) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const parts = new Intl.DateTimeFormat(locale || "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).formatToParts(d);
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value.replace(".", "") ?? "";
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  return `${day} ${month}, ${year}`;
}

export function SparePartLabel({
  qrValue,
  partName,
  partId,
  sourceBrand,
  sourceModel,
  harvestedAt,
  qrSize = 110,
}: SparePartLabelProps) {
  const { i18n, t } = useTranslation("harvest");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && qrValue) {
      QRCode.toCanvas(canvasRef.current, qrValue, { width: qrSize, margin: 1 }, (err) => {
        if (err) console.error("QR generation error:", err);
      });
    }
  }, [qrValue, qrSize]);

  const sourced = [sourceBrand, sourceModel].filter(Boolean).join(" ") || "—";
  const harvestedDate = formatDate(harvestedAt, i18n.language || "en-GB");

  return (
    <div className="bg-card text-card-foreground border border-border rounded-2xl p-5 shadow-lg ring-1 ring-border/50 print:break-inside-avoid print:border-black print:bg-white print:text-black print:rounded-none print:shadow-none print:ring-0 w-full">
      <div className="bg-foreground text-background px-3 py-2 print:bg-black print:text-white">
        <p className="text-lg font-extrabold leading-tight break-words">
          {partName}
        </p>
      </div>
      <div className="mt-3 grid grid-cols-[1fr_auto] gap-4">
        <div className="min-w-0 flex flex-col justify-between gap-3">
          <p className="text-sm font-mono break-all">{partId}</p>
          <div className="space-y-2 text-xs">
            <div>
              <p className="text-muted-foreground">{t("process.label.sourced")}</p>
              <p className="font-mono truncate">{sourced}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("process.label.harvested")}</p>
              <p className="font-mono truncate">{harvestedDate}</p>
            </div>
          </div>
        </div>
        <canvas ref={canvasRef} width={qrSize} height={qrSize} className="flex-shrink-0 self-start" />
      </div>
    </div>
  );
}
